import { forwardRef, useState } from 'react'
import Image from 'next/image'
import cn from 'classnames'
import { CertificateProgramTestimonialSource } from '@mc/api'
import {
  Button,
  IconChevronDownDefault,
  IconChevronUpDefault,
  IconSocialLinkedinActive,
} from '@mc/design-system'
import { useTranslation } from '@mc/i18n'
import { TrackClick } from 'pages/certificates/tracking'
import { type Testimonial } from './types'
import styles from './LinkedInTestimonialCard.module.scss'

interface LinkedInTestimonialCardProps {
  testimonial: Testimonial
}

const toParagraphs = (body?: string): string[] =>
  (body ?? '')
    .split('\n')
    .map((paragraph) => paragraph.trim())
    .filter(Boolean)

export const LinkedInTestimonialCard = forwardRef<
  HTMLDivElement,
  LinkedInTestimonialCardProps
>(({ testimonial }, ref) => {
  const { t } = useTranslation('@mc/certificates')
  const [isExpanded, setIsExpanded] = useState(false)
  const isLinkedIn =
    testimonial.source === CertificateProgramTestimonialSource.LINKEDIN

  return (
    <div
      ref={ref}
      className='rounded-xl overflow-hidden bg-c-base-100 flex flex-col grow'
    >
      <div
        className={cn(
          'bg-c-base-100 p-6 md:p-7 flex flex-col gap-6 grow',
          styles.cardContent,
        )}
      >
        <div className='flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4'>
          {isLinkedIn && (
            <TrackClick
              event='browseCertificatesClick'
              href={testimonial.sourceUrl}
            >
              <a
                href={testimonial.sourceUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='text-c-text-dark shrink-0 inline-flex items-center gap-2 my-3 order-0 md:order-1'
              >
                <IconSocialLinkedinActive style={{ width: 35, height: 35 }} />
                <span className='c-heading-6'>
                  {t(
                    'marketingPages.certificate.linkedInTestimonials.fromLinkedIn',
                  )}
                </span>
              </a>
            </TrackClick>
          )}
          <div className='flex items-center gap-4 order-1 md:order-0'>
            {testimonial.avatarUrl && (
              <Image
                src={testimonial.avatarUrl}
                alt={testimonial.customer?.name ?? ''}
                width={64}
                height={64}
                className='rounded-full shrink-0'
                style={{ objectFit: 'cover' }}
                unoptimized
              />
            )}
            <div className='flex flex-col gap-1'>
              <p className='c-heading-5 text-c-text-dark'>
                {testimonial.customer?.name}
              </p>
              <p className='c-body text-c-text-tint-2'>
                {testimonial.customer?.title}
              </p>
            </div>
          </div>
        </div>

        <div
          className='dotted-border'
          style={{ borderColor: 'var(--c-beige-700)' }}
        />

        {testimonial.quote && (
          <div className='flex gap-4'>
            <div
              className='self-stretch shrink-0'
              style={{
                width: '4px',
                background: 'var(--c-brand-highlight)',
                borderRadius: '2px',
              }}
            />
            <p
              className={cn('c-text-large-bold text-c-text-dark', styles.quote)}
            >
              {testimonial.quote}
            </p>
          </div>
        )}

        <div
          className={cn(styles.textWrapper, {
            [styles.collapsed]: !isExpanded,
          })}
        >
          {toParagraphs(testimonial.body).map((paragraph, i) => (
            <p
              key={i}
              className={cn('c-text-large text-c-text-dark', {
                'mt-4': i > 0,
              })}
            >
              {paragraph}
            </p>
          ))}
          <div
            className='md:hidden'
            style={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '85%',
              background:
                'linear-gradient(to bottom, transparent, var(--c-base-100))',
              pointerEvents: 'none',
              opacity: isExpanded ? 0 : 1,
              transition: 'opacity 0.2s ease-in-out',
            }}
          />
        </div>

        <TrackClick event='browseCertificatesClick'>
          <Button
            kind='tertiary'
            style={{
              gap: '6px',
              cursor: 'pointer',
              border: 'none',
            }}
            className='c-heading-5 text-c-text-tint-2 flex items-center self-start p-0 md:hidden'
            onClick={() => setIsExpanded((prev) => !prev)}
          >
            {isExpanded
              ? t('marketingPages.certificate.linkedInTestimonials.showLess')
              : t('marketingPages.certificate.linkedInTestimonials.showMore')}
            {isExpanded ? (
              <IconChevronUpDefault size='sm' />
            ) : (
              <IconChevronDownDefault size='sm' />
            )}
          </Button>
        </TrackClick>
      </div>

      {testimonial.course?.title && (
        <div className='c-bg-beige-200 px-7 py-6 flex flex-col md:flex-row md:items-center md:justify-between gap-5'>
          <div className='flex flex-col gap-2'>
            <p className='c-caption-mono text-c-text-tint-2'>
              {t(
                'marketingPages.certificate.linkedInTestimonials.certificateEarned',
              )}
            </p>
            <p className='c-heading-5 text-c-text-dark'>
              {testimonial.course.title}
            </p>
          </div>
          {testimonial.course.partnerLogo?.url && (
            <Image
              src={testimonial.course.partnerLogo.url}
              alt={testimonial.course.partnerLogo.altText ?? ''}
              width={180}
              height={60}
              className='shrink-0 self-start md:self-center'
              style={{ objectFit: 'contain', objectPosition: 'left' }}
              unoptimized
            />
          )}
        </div>
      )}
    </div>
  )
})

LinkedInTestimonialCard.displayName = 'LinkedInTestimonialCard'
